import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import Navbar from '@/pages/home/components/Navbar';
import { articleContents } from '@/mocks/articleContents';

const articleRelations: Record<string, string[]> = {
  'basetao-spreadsheet-guide': ['basetao-review', 'basetao-beginner-guide', 'basetao-spreadsheet-tips'],
  'basetao-review': ['is-basetao-legit', 'basetao-vs-superbuy', 'basetao-alternatives'],
  'basetao-shipping-cost': ['basetao-shipping-calculator', 'basetao-usa-shipping', 'basetao-europe-shipping'],
  'basetao-coupon': ['basetao-discount-code', 'budget-basetao-finds', 'basetao-shipping-cost'],
  'basetao-warehouse': ['basetao-qc-photos', 'basetao-tracking-guide', 'basetao-shipping-cost'],
  'is-basetao-legit': ['basetao-review', 'basetao-vs-superbuy', 'basetao-alternatives'],
  'basetao-finds': ['budget-basetao-finds', 'basetao-spreadsheet-tips', 'basetao-review'],
  'basetao-alternatives': ['basetao-vs-superbuy', 'basetao-vs-sugargoo', 'basetao-vs-pandabuy'],
  'basetao-shipping-calculator': ['basetao-shipping-cost', 'basetao-usa-shipping', 'basetao-europe-shipping'],
  'basetao-beginner-guide': ['basetao-spreadsheet-guide', 'basetao-qc-photos', 'basetao-warehouse'],
  'basetao-vs-superbuy': ['basetao-vs-sugargoo', 'basetao-vs-pandabuy', 'basetao-alternatives'],
  'basetao-vs-sugargoo': ['basetao-vs-superbuy', 'basetao-vs-pandabuy', 'budget-basetao-finds'],
  'basetao-vs-pandabuy': ['basetao-vs-superbuy', 'basetao-vs-sugargoo', 'basetao-alternatives'],
  'basetao-europe-shipping': ['basetao-shipping-cost', 'basetao-shipping-calculator', 'basetao-usa-shipping'],
  'basetao-usa-shipping': ['basetao-shipping-cost', 'basetao-shipping-calculator', 'basetao-europe-shipping'],
  'basetao-qc-photos': ['basetao-warehouse', 'basetao-beginner-guide', 'basetao-review'],
  'basetao-tracking-guide': ['basetao-warehouse', 'basetao-usa-shipping', 'basetao-europe-shipping'],
  'budget-basetao-finds': ['basetao-finds', 'basetao-coupon', 'basetao-discount-code'],
  'basetao-spreadsheet-tips': ['basetao-spreadsheet-guide', 'basetao-finds', 'budget-basetao-finds'],
  'basetao-discount-code': ['basetao-coupon', 'budget-basetao-finds', 'basetao-shipping-cost'],
};

function getRelatedArticles(currentSlug: string) {
  const relatedSlugs = articleRelations[currentSlug] || [];
  return relatedSlugs
    .map((s) => {
      const a = articleContents[s];
      return a ? { title: a.title, slug: a.slug } : null;
    })
    .filter(Boolean) as { title: string; slug: string }[];
}

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const article = articleContents[slug];
  const relatedArticles = article ? getRelatedArticles(slug) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (!article) {
    return (
      <main className="min-h-screen">
        <Navbar />
        <div className="pt-20 pb-16 text-center">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-4-line"></i></span>
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    author: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
    datePublished: '2026-08-10',
    dateModified: '2026-08-10',
    publisher: { '@type': 'Organization', name: 'Basetao Spreadsheet Hub' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${VITE_SITE_URL}/${article.slug}` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${VITE_SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: article.title, item: `${VITE_SITE_URL}/${article.slug}` },
    ],
  };

  return (
    <main className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <article className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium truncate">{article.title}</span>
          </nav>

          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-sm text-foreground-400">Updated August 10, 2026</span>
              <span className="text-sm text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.intro}
            </p>
          </header>

          <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden bg-background-100 mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
          </div>

          {article.sections.map((section, sIdx) => (
            <section key={sIdx} id={`section-${sIdx + 1}`} className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {sIdx + 1}. {section.heading}
              </h2>

              {section.content.map((paragraph, pIdx) => (
                <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: paragraph }}></p>
              ))}

              {section.listItems && (
                <div className="bg-background-100 rounded-xl p-5 mb-4">
                  <ol className="space-y-3">
                    {section.listItems.map((item, iIdx) => (
                      <li key={iIdx} className="flex gap-3">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold flex-shrink-0 mt-0.5">{iIdx + 1}</span>
                        <span className="text-sm text-foreground-700">{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {section.tableData && (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        {section.tableData.headers.map((h, hIdx) => (
                          <th key={hIdx} className="text-left py-3 pr-4 font-semibold text-foreground-900 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.tableData.rows.map((row, rIdx) => (
                        <tr key={rIdx} className="border-b border-background-100">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className={`py-2.5 pr-4 whitespace-nowrap ${cIdx === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.tips && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {section.tips.map((tip, tIdx) => (
                    <div key={tIdx} className="bg-background-100 rounded-xl p-4">
                      <h4 className="font-semibold text-foreground-900 text-sm mb-2 flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-lightbulb-line text-sm text-primary-500"></i>
                        </span>
                        {tip.title}
                      </h4>
                      <p className="text-xs text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          {article.faqs.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {article.faqs.map((faq, fIdx) => (
                  <div key={fIdx} className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">{faq.q}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {relatedArticles.length > 0 && (
            <section className="mb-10 bg-background-100 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-link-m text-primary-500"></i>
                </span>
                Related Basetao Spreadsheet Articles
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/${ra.slug}`}
                    className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:shadow-sm transition-all duration-200 cursor-pointer group"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-200 transition-colors flex-shrink-0 mt-0.5">
                      <i className="ri-article-line text-sm text-primary-600"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug">
                      {ra.title}
                    </span>
                  </Link>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-background-200 flex flex-wrap items-center gap-4 text-sm">
                <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  ← Back to Basetao Spreadsheet Hub
                </Link>
                <Link to="/basetao-spreadsheet-guide" className="text-foreground-600 hover:text-primary-600 transition-colors">
                  Ultimate Guide
                </Link>
                <Link to="/basetao-review" className="text-foreground-600 hover:text-primary-600 transition-colors">
                  Basetao Review
                </Link>
                <Link to="/basetao-shipping-cost" className="text-foreground-600 hover:text-primary-600 transition-colors">
                  Shipping Guide
                </Link>
              </div>
            </section>
          )}

          <div className="mt-12 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
              {article.ctaText}
            </h2>
            <p className="text-primary-100 mb-6 max-w-lg mx-auto">
              Explore thousands of curated products across every category on the Basetao Spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-700 font-semibold text-base hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line"></i>
                </span>
                Basetao Spreadsheet
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-primary-300 text-background-50 font-semibold text-base hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}